import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { Table, Button, Input, Space, Tag, message } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import AddModal from "./AddModal";
import Layout from '../../Layout'
import {
    Edit,
    X,
} from "lucide-react";
import { mixDate, mixDelV1, mixGetV1, mixPostV1, mixPutV1 } from "services/mixin";
import useAuthStore from "../../store/authStore";
import useTableColumnSettings, { SettingColumn } from "../../hooks/useTableColumnSettings";
import { ColumnSettingButton } from "../../components/TableColumnSetting";

interface DepartmentItem {
    id: number;
    name: string;
    description: string;
    status: number;
    created_at: string | null;
    updated_at: string | null;
    deleted_at: string | null;
}

export default function DepartmentManagement() {
    const { t } = useTranslation();
    // Phân quyền thao tác: chỉ có quyền xem thì không hiện nút thêm/sửa/xóa
    const { isSuperAdmin, hasPermission } = useAuthStore();
    const canCreate = isSuperAdmin || hasPermission("departments.create");
    const canEdit = isSuperAdmin || hasPermission("departments.edit");
    const canDelete = isSuperAdmin || hasPermission("departments.delete");
    const [searchTerm, setSearchTerm] = useState("");
    const [modalOpen, setModalOpen] = useState(false);
    const [mode, setMode] = useState<"add" | "edit">("add");
    const [editingItem, setEditingItem] = useState<DepartmentItem | null>(null);

    const [data, setData] = useState<DepartmentItem[]>([]);
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [sortParam, setSortParam] = useState<string>("id,desc");
    const [submitLoading, setSubmitLoading] = useState(false);

    const [actionModal, setActionModal] = useState<{ open: boolean; type: "approve" | "reject" | null; id: number | null }>({ open: false, type: null, id: null });

    const openDeleteModal = (id: number) => {
        setActionModal({ open: true, type: "reject", id });
    };

    const handleCloseActionModal = () => {
        setActionModal({ open: false, type: null, id: null });
    };

    const handleConfirmAction = async () => {
        if (actionModal.id !== null) {
            setSubmitLoading(true);
            const res = await mixDelV1(`/api/v1/be/departments/${actionModal.id}`, { _method: "DELETE" });

            if (res.status) {
                message.success(t('departments.delete_success'));
                getList();
            } else {
                message.error(t('departments.delete_failed'));
            }
            setSubmitLoading(false);
        }
        handleCloseActionModal();
    };

    const openAdd = () => {
        setMode("add");
        setEditingItem(null);
        setModalOpen(true);
    };

    const openEdit = (record: DepartmentItem) => {
        setMode("edit");
        setEditingItem(record);
        setModalOpen(true);
    };

    const handleSubmit = async (values: any) => {
        setSubmitLoading(true);
        const param = {
            ...values,
            status: values.is_active ? 0 : 1,
        };
        delete param.is_active;

        if (mode === "add") {
            const res = await mixPostV1('/api/v1/be/departments', param);

            if (res.status) {
                message.success(t('departments.add_success'));
                getList();
            } else {
                message.error(t('departments.add_failed'));
            }
        } else if (mode === "edit" && editingItem) {
            const res = await mixPutV1(`/api/v1/be/departments/${editingItem.id}`, param);

            if (res.status) {
                message.success(t('departments.edit_success'));
                getList();
            } else {
                message.error(t('departments.edit_failed'));
            }
        }

        setSubmitLoading(false);
        setModalOpen(false);
    };

    const columns: SettingColumn<DepartmentItem>[] = [
        {
            title: "Id",
            dataIndex: "id",
            key: "id",
            sorter: true,
        },
        {
            title: t('departments.col_name'),
            dataIndex: "name",
            key: "name",
            sorter: true,
        },
        {
            title: t('departments.col_description'),
            dataIndex: "description",
        },
        {
            title: t('departments.col_status'),
            key: "status",
            render: (_, record) => (
                <Tag color={record.status === 0 ? "green" : "red"}>
                    {record.status === 0 ? t('departments.status_active') : t('departments.status_inactive')}
                </Tag>
            ),
        },
        {
            title: t('departments.col_created_at'),
            key: "created_at",
            sorter: true,
            render: (_, record) => mixDate(record.created_at as any, { hour: true }),
        },
        {
            key: "actions",
            title: t('departments.col_action'),
            fixed: "right",
            lockVisible: true,
            render: (_, record) => (
                <div className="flex items-center gap-3">
                    <div className="flex items-center space-x-3 gap-2">
                        {canEdit && (
                            <button
                                onClick={() => openEdit(record)}
                                className="!text-[#3e91ab] hover:text-yellow-700 bg-yellow-50 hover:bg-yellow-100 dark:bg-yellow-900/30 dark:hover:bg-yellow-900/50 p-1.5 rounded-md transition-colors"
                                title={t('departments.btn_edit')}
                            >
                                <Edit className="h-4 w-4" />
                            </button>
                        )}
                        {canDelete && (
                            <button
                                onClick={() => openDeleteModal(record.id)}
                                className="!text-red-500 hover:text-red-700 bg-red-50 hover:bg-red-100 dark:bg-red-900/30 dark:hover:bg-red-900/50 p-1.5 rounded-md transition-colors"
                                title={t('departments.btn_delete')}
                            >
                                <X className="h-4 w-4" />
                            </button>
                        )}
                    </div>
                </div>
            ),
        },
    ];

    // Ẩn/hiện, sắp xếp, cố định, độ rộng cột — lưu theo tài khoản (extra_data)
    const {
        columns: tableColumns,
        components: tableComponents,
        tableLayout,
        scrollX,
        settingProps: columnSettingProps,
    } = useTableColumnSettings(
        "department_list",
        (canEdit || canDelete) ? columns : columns.filter((c) => c.key !== "actions")
    );

    // --------------- API ------------------------
    const getList = async () => {
        setLoading(true);

        const param = {
            page,
            search: searchTerm,
            sort: sortParam,
        };

        const res = await mixGetV1('/api/v1/be/departments', param);

        if (res.status) {
            setData(res.data.items);
            setTotal(res.data.total);
        }
        setLoading(false);
    };

    useEffect(() => {
        getList();
    }, [page, searchTerm, sortParam]);

    return (
        <Layout>
            <Helmet>
                <title>{t('page.departments')} | Khothi</title>
            </Helmet>
            <div className="">
                <div className="flex gap-2 flex-col md:flex-row md:justify-between mb-4">
                    <h2 className="text-lg text-gray-900 font-semibold t-text-white">
                        {t('departments.title')}
                    </h2>

                    <Space>
                        <div className="relative flex-1">
                            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="lucide lucide-search h-5 w-5 text-gray-400" aria-hidden="true"><path d="m21 21-4.34-4.34"></path><circle cx="11" cy="11" r="8"></circle></svg>
                            </div>
                            <input
                                type="text"
                                onChange={(e) => {
                                    setSearchTerm(e.target.value);
                                    setPage(1);
                                }}
                                className="block w-full pl-10 pr-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md leading-5 bg-white dark:bg-gray-700 text-gray-900 t-text-white placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-[#3e91ab] focus:border-[#3e91ab] sm:text-sm transition-colors h-[32px]"
                                placeholder={t('departments.search')}
                            />
                        </div>
                        {canCreate && (
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={openAdd}
                                className="!bg-[#3e91ab] hover:!bg-[#357f96] border-none"
                            >
                                {t('departments.add_new')}
                            </Button>
                        )}
                        <ColumnSettingButton {...columnSettingProps} />
                    </Space>
                </div>

                <Table
                    loading={loading}
                    rowKey="id"
                    columns={tableColumns}
                    components={tableComponents}
                    tableLayout={tableLayout}
                    dataSource={data}
                    scroll={{ x: scrollX }}
                    className="custom-ant-table"
                    onChange={(_pagination, _filters, sorter: any, extra: any) => {
                        // onChange của Table bắn cho cả phân trang lẫn sắp xếp. Nếu không lọc
                        // theo action thì bấm sang trang 2 sẽ bị chính handler này kéo về trang 1.
                        if (extra?.action !== 'sort') return;
                        if (sorter?.field && sorter?.order) {
                            const direction = sorter.order === 'ascend' ? 'asc' : 'desc';
                            setSortParam(`${sorter.field},${direction}`);
                        } else if (sorter?.columnKey && sorter?.order) {
                            const direction = sorter.order === 'ascend' ? 'asc' : 'desc';
                            setSortParam(`${sorter.columnKey},${direction}`);
                        } else {
                            setSortParam('id,desc');
                        }
                        setPage(1);
                    }}
                    pagination={{
                        current: page,
                        total: total,
                        pageSize: 10,
                        onChange: (p) => setPage(p),
                    }}
                />

                <AddModal
                    open={modalOpen}
                    mode={mode}
                    initialValues={
                        editingItem
                            ? {
                                name: editingItem.name,
                                description: editingItem.description,
                                is_active: editingItem.status === 0,
                            }
                            : undefined
                    }
                    onCancel={() => setModalOpen(false)}
                    onSubmit={handleSubmit}
                    loading={submitLoading}
                />

                {/* Delete Confirmation Modal */}
                {actionModal.open && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
                        <div className="bg-white dark:bg-gray-800 rounded-md shadow-xl w-full max-w-md">
                            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
                                <h3 className="text-lg font-bold text-gray-900 t-text-white">
                                    {t('departments.delete_title')}
                                </h3>
                                <p className="text-sm text-gray-500 text-000000e0 mt-1">
                                    {t('departments.delete_confirm')}
                                </p>
                            </div>
                            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end gap-3">
                                <button
                                    onClick={handleCloseActionModal}
                                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg !text-gray-700 dark:!text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
                                >
                                    {t('departments.cancel')}
                                </button>
                                <button
                                    onClick={handleConfirmAction}
                                    disabled={submitLoading}
                                    className="px-4 py-2 !text-white rounded-lg transition bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {submitLoading ? '...' : t('departments.delete')}
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </Layout>
    );
}